import React, { useState, useEffect } from 'react'
import { Shield, Lock, Eye, TrendingUp, RefreshCw, Award, CheckCircle, Code } from 'lucide-react'

interface RankTrack {
  score: number
  tier: string
  next_tier: { tier: string; points_needed: number } | null
}

interface Outcome {
  id: string
  skill_id: string
  skill_version: string
  tracks: string[]
  framework_mappings: any[]
  coverage_pct: number
  confidence: number
  signature_verified: boolean
  assessed_at: string
  valid_until: string
}

interface TrendPoint {
  date: string
  track: string
  rank_eod: number
}

interface RankData {
  tenant_id: string
  agent_id: string | null
  ranks: {
    compliance: RankTrack
    security: RankTrack
    privacy: RankTrack
  }
  likelihood_of_pass: Record<string, number>
  active_outcomes: number
  recent_outcomes: Outcome[]
  trend_30d: TrendPoint[]
  computed_at: string
  improve: {
    note: string
    multipliers: Record<string, string>
  }
}

interface SkillsRankTabProps {
  tenantId: string
  getHeaders: () => HeadersInit
}

const TIER_CONFIG: Record<string, { color: string; bg: string; border: string; icon: string }> = {
  grandmaster: { color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  icon: '♛' },
  advanced:    { color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', icon: '◆' },
  proficient:  { color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200', icon: '▲' },
  established: { color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   icon: '●' },
  provisional: { color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: '○' },
  unranked:    { color: 'text-gray-500',   bg: 'bg-gray-50',   border: 'border-gray-200',   icon: '—' },
}

const TRACK_INFO = {
  compliance: { label: 'Compliance Rank', symbol: 'CR', Icon: Shield, description: 'SOC2, ISO 27001, FedRAMP, PCI-DSS, HIPAA' },
  security:   { label: 'Security Rank',   symbol: 'SR', Icon: Lock,   description: 'Vuln scans, threat models, pen tests' },
  privacy:    { label: 'Privacy Rank',    symbol: 'PR', Icon: Eye,    description: 'GDPR, CCPA, data flows, consent audits' },
} as const

// Tier min/max for progress bar
const TIER_BOUNDS: Record<string, [number, number]> = {
  grandmaster: [2400, 5000],
  advanced:    [1800, 2400],
  proficient:  [1000, 1800],
  established: [400,  1000],
  provisional: [1,    400],
  unranked:    [0,    1],
}

function tierProgressPct(score: number, tier: string): number {
  const [min, max] = TIER_BOUNDS[tier] ?? [0, 1]
  if (max === min) return 0
  return Math.min(100, Math.max(0, Math.round(((score - min) / (max - min)) * 100)))
}

function likelihoodColor(p: number): string {
  if (p >= 0.80) return 'text-green-700 bg-green-50 border-green-100'
  if (p >= 0.60) return 'text-blue-700 bg-blue-50 border-blue-100'
  if (p >= 0.40) return 'text-yellow-700 bg-yellow-50 border-yellow-100'
  return 'text-red-700 bg-red-50 border-red-100'
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface BenchmarkData {
  tenant_percentiles: { compliance: number; security: number; privacy: number }
  population: {
    total_entities: number
    compliance: { p25: number; p50: number; p75: number; mean: number }
    security:   { p25: number; p50: number; p75: number; mean: number }
    privacy:    { p25: number; p50: number; p75: number; mean: number }
  }
}

export default function SkillsRankTab({ tenantId, getHeaders }: SkillsRankTabProps) {
  const [data, setData] = useState<RankData | null>(null)
  const [benchmark, setBenchmark] = useState<BenchmarkData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCode, setShowCode] = useState(false)

  useEffect(() => { load() }, [tenantId])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [rankRes, benchRes] = await Promise.all([
        fetch('/api/skills/rank', { headers: getHeaders() }),
        fetch('/api/skills/rank/benchmark', { headers: getHeaders() }),
      ])
      if (rankRes.status === 401) throw new Error('Authentication required — generate an API key in Settings → API Keys')
      if (!rankRes.ok) throw new Error('Failed to load rank data')
      setData(await rankRes.json())
      if (benchRes.ok) setBenchmark(await benchRes.json())
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return (
    <div className="text-center py-12 text-gray-500">
      <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
      <p className="text-sm">Loading rank data...</p>
    </div>
  )

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
  )

  if (!data) return null

  const hasOutcomes = data.active_outcomes > 0
  const frameworks = Object.entries(data.likelihood_of_pass ?? {})

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Skills & Rank</h2>
          <p className="text-sm text-gray-500 mt-1">
            {hasOutcomes
              ? `${data.active_outcomes} active outcome${data.active_outcomes !== 1 ? 's' : ''} · Computed ${new Date(data.computed_at).toLocaleString()}`
              : 'No skill outcomes yet — submit your first outcome to start building rank'}
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Rank Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(['compliance', 'security', 'privacy'] as const).map(track => {
          const info = TRACK_INFO[track]
          const rank = data.ranks[track]
          const tier = TIER_CONFIG[rank.tier] ?? TIER_CONFIG.unranked
          const { Icon } = info
          const pct = tierProgressPct(rank.score, rank.tier)

          return (
            <div key={track} className={`bg-white rounded-xl border ${tier.border} p-5`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">{info.label}</p>
                    <p className="text-xs text-gray-400">{info.symbol}</p>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${tier.bg} ${tier.color}`}>
                  {tier.icon} {capitalize(rank.tier)}
                </span>
              </div>

              <div className="mb-4">
                <p className="text-3xl font-bold text-gray-900 tabular-nums">{rank.score.toLocaleString()}</p>
                <p className="text-xs text-gray-400">points</p>
              </div>

              {/* Tier progress bar */}
              <div className="mb-2">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${rank.tier === 'unranked' ? 'bg-gray-300' : 'bg-blue-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {rank.next_tier ? (
                <p className="text-xs text-gray-400">
                  <span className="font-semibold text-gray-600">{rank.next_tier.points_needed.toLocaleString()} pts</span>
                  {' '}to {rank.next_tier.tier}
                </p>
              ) : (
                <p className="text-xs text-amber-600 font-semibold">Maximum tier achieved</p>
              )}

              <p className="text-xs text-gray-400 mt-3">{info.description}</p>
            </div>
          )
        })}
      </div>

      {/* Likelihood of Pass */}
      {frameworks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Likelihood of Audit Pass</h3>
          <p className="text-xs text-gray-400 mb-4">
            Based on current rank score and recent outcome coverage per framework.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {frameworks.map(([framework, probability]) => (
              <div key={framework} className={`rounded-lg border p-3 ${likelihoodColor(probability)}`}>
                <p className="text-xs font-medium truncate uppercase tracking-wide">
                  {framework.replace(/_/g, ' ')}
                </p>
                <p className="text-2xl font-bold mt-1 tabular-nums">{Math.round(probability * 100)}%</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Calibrated sigmoid: P(pass) = f(rank) × coverage_bonus. Increase coverage by submitting
            more outcomes for each framework.
          </p>
        </div>
      )}

      {/* Industry Benchmark */}
      {benchmark && benchmark.population.total_entities > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Industry Benchmark</h3>
          <p className="text-xs text-gray-400 mb-4">
            Your percentile vs {benchmark.population.total_entities.toLocaleString()} registered entities in the TrustCenterPage registry.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {([
              { key: 'compliance', label: 'Compliance', Icon: Shield },
              { key: 'security',   label: 'Security',   Icon: Lock   },
              { key: 'privacy',    label: 'Privacy',    Icon: Eye    },
            ] as const).map(({ key, label, Icon }) => {
              const pct = benchmark.tenant_percentiles[key]
              const pop = benchmark.population[key]
              return (
                <div key={key} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">{label}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">{pct}th</p>
                  <p className="text-xs text-gray-400">percentile</p>
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-gray-400">
                    <span>p50: {pop.p50}</span>
                    <span>p75: {pop.p75}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Outcomes */}
      {hasOutcomes && data.recent_outcomes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Recent Skill Outcomes</h3>
            <span className="text-xs text-gray-400">{data.recent_outcomes.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Skill</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Tracks</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Coverage</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Confidence</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Valid Until</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">Verified</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.recent_outcomes.map(outcome => (
                  <tr key={outcome.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900 text-xs">{outcome.skill_id}</p>
                      <p className="text-gray-400 text-xs">v{outcome.skill_version}</p>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(outcome.tracks ?? []).map(t => (
                          <span key={t} className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-700 tabular-nums">
                      {Math.round((outcome.coverage_pct ?? 0) * 100)}%
                    </td>
                    <td className="px-5 py-3 text-gray-700 tabular-nums">
                      {Math.round((outcome.confidence ?? 0) * 100)}%
                    </td>
                    <td className="px-5 py-3 text-gray-500 text-xs">
                      {new Date(outcome.valid_until).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">
                      {outcome.signature_verified
                        ? <CheckCircle className="h-4 w-4 text-green-500" />
                        : <span className="text-xs text-gray-400">Self-assessed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 30d Trend */}
      {data.trend_30d.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-gray-400" />
            30-Day Rank Trend
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left py-2 pr-6">Date</th>
                  <th className="text-left py-2 pr-6">Track</th>
                  <th className="text-right py-2">End-of-Day Rank</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.trend_30d.slice(-30).map((row, i) => (
                  <tr key={i} className="text-gray-600">
                    <td className="py-1.5 pr-6">{row.date}</td>
                    <td className="py-1.5 pr-6 capitalize">{row.track}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">
                      {Number(row.rank_eod).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasOutcomes && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
          <Award className="h-14 w-14 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Building Your Rank</h3>
          <p className="text-sm text-gray-500 max-w-lg mx-auto mb-6">
            Submit skill outcomes to accumulate Compliance (CR), Security (SR), and Privacy (PR) rank.
            Skills are free. Rank is built continuously. Third parties pay to verify.
          </p>
          <button
            onClick={() => setShowCode(v => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Code className="h-4 w-4" />
            {showCode ? 'Hide example' : 'Show submission example'}
          </button>
        </div>
      )}

      {/* Submission Instructions */}
      {(showCode || hasOutcomes) && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Submitting Skill Outcomes</h3>
          <p className="text-xs text-gray-500 mb-3">
            Use the <code className="bg-gray-200 px-1 rounded font-mono">@trustcenter/skill-user-access-review</code> package
            or any compatible skill. Outcomes must include a SHA256 <code className="bg-gray-200 px-1 rounded font-mono">outcome_id</code>.
          </p>
          <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-xs space-y-1 overflow-x-auto">
            <div className="text-gray-500"># Install the skill</div>
            <div>npm install -g @trustcenter/skill-user-access-review</div>
            <div className="mt-2 text-gray-500"># Run + auto-submit to registry</div>
            <div>trustcenter-uar --tenant your-domain.com \</div>
            <div>  --users ./users.json \</div>
            <div>  --api-key tc_your_key \</div>
            <div>  --submit</div>
            <div className="mt-2 text-gray-500"># Or POST outcome JSON directly</div>
            <div>POST /api/skills/submit</div>
            <div>Authorization: Bearer tc_your_api_key</div>
            <div>Content-Type: application/json</div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Get your API key in <strong>Settings → API Keys</strong>.
            All outcomes stored free. Third parties pay to query verified evidence.
          </p>
        </div>
      )}

      {/* Multipliers */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Rank Multipliers</h3>
        <p className="text-xs text-gray-500 mb-3">{data.improve?.note}</p>
        <div className="divide-y divide-gray-50">
          {Object.entries(data.improve?.multipliers ?? {}).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2.5">
              <span className="text-xs text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
              <span className="text-xs font-semibold text-gray-900 font-mono">{value}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}
